import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import { exchangeCalendarApis } from '@apis/next/exchangeCalendarApis';
import { dashboardInvoiceApis, invoiceApis } from '@apis/next/invoiceApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import {
  type InvoiceFormDialogContextValuesBase,
  type InvoiceFormDialogContextValues,
  InvoiceFormDialogContext,
} from '@components/admin/booking/helpers2';
import { InvoiceDetailsTable } from '@components/admin/booking/InvoiceDetailsTable';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { DebugForm } from '@components/NextForms/DebugForm';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { calendarStrings } from '@data/calendar';
import { invoiceStrings } from '@data/invoice';
import { useClientIdAutoComplete } from '@hooks/next/useClientIdAutoComplete';
import { useBlobURL } from '@hooks/useBlobURL';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMutation } from '@hooks/useMutation';
import { useStateFromQuery } from '@hooks/useStateFromQuery';
import { countryStrings } from '@models/Country';
import {
  Invoice,
  InvoiceChargePayDayForm,
  // InvoiceDetails,
  InvoiceErrorMessage,
  InvoiceInfoForm,
  InvoiceStatusName,
} from '@models/next/Invoice';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { empty, getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { mapOptional } from '@utils/types';
import { useShallowMemo } from '@utils/useShallowMemo';
import { isErrorFromPath } from '@zodios/core';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { Document, Page } from 'react-pdf';

const strings = createStrings({
  ja: {
    title: {
      create: `${invoiceStrings.invoice}を作成する`,
      update: `${invoiceStrings.invoice}を編集する`,
    },
    cannotCreateInvoice: `下記の原因で${invoiceStrings.invoice}を作成できません：`,
    inputPrompt: '下記情報を入力してください',
    customerAddress: '請求先の住所',
    noInvoiceForPayerMark: `${invoiceStrings.payer_mark}の名目はありません。再選択してください。`,
    selectInvoiceDetailsPrompt: '請求明細から選んでください',
    postInvoiceSuccessMessage: (invoice: Invoice) => (
      <>
        お疲れ様です！ <br />
        基幹請求番号を無事に獲得されました。
        <br />
        請求書は基幹システムとお客様に送信されました。
        <br />
        基幹請求番号：<b>{invoice.charge_no}</b>
        <br />
      </>
    ),
    postInvoiceDraftMessage: () => (
      <>
        請求書は一時保存されました。
        <br />
      </>
    ),
    postInvoiceErrorMessage: (error: string) => (
      <>
        基幹請求番号を獲得できませんでした。 <br />
        理由： <br />
        <code>{error}</code>
        <br />
        請求書の内容を調整して、もう一度お試しください。
        <br />
        または、FC IT管理者に連絡してください。
        <br />
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1.5rem 100px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'scroll',
    maxHeight: 550,
  },
  form: {
    ...common.flexColumn,
    flexShrink: 1,
    minHeight: 0,
  },
  resultContainer: {
    ...common.normalText,
    ...common.textGray700,
    textAlign: 'center',
    padding: '100px 0',
  },
}));

const InvoiceChargePayDayStep: React.FC = observer(() => {
  const {
    onClose,
    invoiceChargePayDayForm,
    setInvoiceChargePayDayForm,
    invoiceInfoForm,
    setInvoiceInfoForm,
    invoiceDefaultValue,
    step,
    setStep,
    setSelectedInvoiceDetails,
  } = useContext(InvoiceFormDialogContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(InvoiceChargePayDayForm, {
    initialOutput:
      invoiceChargePayDayForm ??
      InvoiceHelper.mapDefaultValueToChargePayDayForm(invoiceDefaultValue!),
  });

  const {
    root: { fields },
  } = form;

  const getChargePayDay = useMutation(async (values: InvoiceChargePayDayForm) => {
    return await dashboardInvoiceApis.client.get('/dashboard/invoice/charge-pay-day', {
      queries: {
        charge_date: values.charge_date,
        charge_client_code: values.client_code,
        d2d_no: values.house_bl_number,
      },
    });
  });

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [getChargePayDay] });

  const clientIdAutoComplete = useClientIdAutoComplete(fields);

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          const chargePayDayResponse = await getChargePayDay.mutateAsync(values);

          setInvoiceChargePayDayForm(values);
          setInvoiceInfoForm(
            invoiceInfoForm
              ? { ...invoiceInfoForm, ...chargePayDayResponse }
              : InvoiceHelper.mapDefaultValueAndChargePayDayToInfoForm(
                  invoiceDefaultValue!,
                  chargePayDayResponse,
                ),
          );

          // If payer_mark is changed, all selected invoice details should be voided.
          // Because we only show invoice details for the current payer_mark at one time.
          if (values.payer_mark !== invoiceChargePayDayForm?.payer_mark) {
            setSelectedInvoiceDetails([]);
          }
          setStep(step + 1);
        },
      })}
    >
      <div className={classes.dialogContent}>
        <IndexedFormContainer>
          <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>

          <div className={clsx(classes.colSpanFull, classes.textGray700, classes.middleText)}>
            {strings.inputPrompt}
          </div>

          <IndexedLabel index={1} field={fields.house_bl_number} />
          <div>
            <TextInput field={fields.house_bl_number} disabled />
          </div>

          <IndexedLabel index={2} field={fields.charge_date} />
          <div>
            <TextInput field={fields.charge_date} />
          </div>

          <IndexedLabel index={3} field={fields.client_id} />
          <div>
            <TextInput field={fields.client_name} autoCompleteConfig={clientIdAutoComplete} />
          </div>

          <IndexedLabel field={fields.payer_mark} />
          <div>
            <TextInput
              field={fields.payer_mark}
              inputOptions={{
                type: 'choose',
                options: [
                  { label: countryStrings.jp, value: 'jp' },
                  { label: countryStrings.cn, value: 'cn' },
                ],
              }}
            />
          </div>

          <IndexedLabel label={strings.customerAddress} />
          <div>
            <TextInput field={fields.client_zipcode} />
          </div>

          <IndexedLabel label="" />
          <div>
            <TextInput field={fields.client_address1} />
          </div>

          <IndexedLabel label="" />
          <div>
            <TextInput field={fields.client_address2} />
          </div>
        </IndexedFormContainer>
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={onClose}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="submit"
          text={formStrings.next}
          style={{ width: 90 }}
        />
        <DebugForm form={form} />
      </ActionableDialogActions>
    </form>
  );
});

const InvoiceInfoStep: React.FC = observer(() => {
  const { invoiceInfoForm, setInvoiceInfoForm, step, setStep, currencyRates } =
    useContext(InvoiceFormDialogContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(InvoiceInfoForm, {
    initialOutput: invoiceInfoForm ?? empty,
    plugins: [
      {
        name: 'autoFillConversionRate',
        onBeforeSubmit() {
          const targetConversionRate = currencyRates.find(
            (r) => r.date === getDecodeResult(fields.conversion_rate_date.decodeResult),
          );

          if (targetConversionRate) {
            fields.conversion_rate.setOutput(targetConversionRate.rate.toString());
          } else {
            fields.conversion_rate_date.setOutput('');
          }
        },
      },
    ],
  });

  const {
    root: { fields },
  } = form;

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          setInvoiceInfoForm(values);
          setStep(step + 1);
        },
      })}
    >
      <div className={classes.dialogContent}>
        <IndexedFormContainer>
          <div className={classes.colSpanFull}></div>

          <div className={clsx(classes.colSpanFull, classes.textGray700, classes.middleText)}>
            {strings.inputPrompt}
          </div>

          <IndexedLabel index={4} field={fields.invoice_no} />
          <div>
            <TextInput field={fields.invoice_no} disabled />
          </div>

          <IndexedLabel index={5} field={fields.job_code} />
          <div>
            <TextInput field={fields.job_code} disabled />
          </div>

          <IndexedLabel index={6} field={fields.charge_pay_day} />
          <div>
            <TextInput field={fields.charge_pay_day} disabled />
          </div>

          <IndexedLabel index={7} field={fields.vessel_voyage} />
          <div>
            <TextInput field={fields.vessel_voyage} />
          </div>

          <IndexedLabel index={8} field={fields.transport_route} />
          <div>
            <TextInput field={fields.transport_route} />
          </div>

          <IndexedLabel index={9} field={fields.ata} />
          <div>
            <TextInput field={fields.ata} />
          </div>

          <IndexedLabel index={10} field={fields.quantity_packages} />
          <div>
            <TextInput field={fields.quantity_packages} />
          </div>

          <IndexedLabel index={11} field={fields.customer_no} />
          <div>
            <TextInput field={fields.customer_no} />
          </div>

          <IndexedLabel index={12} field={fields.conversion_rate} />
          <div>
            <TextInput
              field={fields.conversion_rate_date}
              inputOptions={useMemo(
                () => ({
                  type: 'choose',
                  options: [
                    {
                      value: empty,
                      label: '-',
                    },
                    ...currencyRates.map(({ date, rate }) => ({
                      value: date,
                      label: `1 USD = ${rate} JPY ${date} ${calendarStrings.weekdaysShort(
                        new Date(date).getDay(),
                      )}`,
                    })),
                  ],
                }),
                [currencyRates],
              )}
            />
          </div>

          <IndexedLabel index={12} field={fields.language} />
          <div>
            <TextInput field={fields.language} />
          </div>
        </IndexedFormContainer>
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={() => setStep(step - 1)}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="submit"
          text={formStrings.next}
          style={{ width: 90 }}
        />
        <DebugForm form={form} />
      </ActionableDialogActions>
    </form>
  );
});

const InvoiceDetailsStep: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    step,
    setStep,
    id,
    invoiceDetails,
    selectedInvoiceDetails,
    setSelectedInvoiceDetails,
    invoiceChargePayDayForm,
  } = useContext(InvoiceFormDialogContext);

  const currentInvoiceDetails = useMemo(
    () => invoiceDetails.filter((d) => d.payer_mark === invoiceChargePayDayForm?.payer_mark),
    [invoiceDetails],
  );

  const selectedIds = useMemo(
    () => selectedInvoiceDetails.map(({ id }) => id),
    [selectedInvoiceDetails],
  );

  const disabledIds = useMemo(
    () =>
      currentInvoiceDetails
        .filter((d) => d.unit_num == null || d.unit_price == null)
        .map(({ id }) => id),
    [currentInvoiceDetails],
  );

  const usedIds = useMemo(
    () =>
      currentInvoiceDetails
        .filter((d) => (id == null ? d.invoice != null : d.invoice != null && d.invoice != id))
        .map(({ id }) => id),
    [currentInvoiceDetails],
  );

  const allSelectableIds = useMemo(
    () =>
      currentInvoiceDetails
        .filter(
          (d) =>
            d.unit_num != null &&
            d.unit_price != null &&
            d.unit_num !== 0 &&
            d.unit_price !== 0 &&
            !usedIds.includes(d.id),
        )
        .map(({ id }) => id),
    [currentInvoiceDetails, usedIds],
  );

  return (
    <div className={classes.form}>
      <div className={classes.dialogContent}>
        <div className={clsx(classes.colSpanFull, classes.textGray700, classes.middleText)}>
          {strings.selectInvoiceDetailsPrompt}
        </div>

        <InvoiceDetailsTable
          selectedIds={selectedIds}
          usedIds={usedIds}
          disabledIds={disabledIds}
          allSelectableIds={allSelectableIds}
          items={currentInvoiceDetails}
          onChangeSelectedIds={(ids) =>
            setSelectedInvoiceDetails(currentInvoiceDetails.filter(({ id }) => ids.includes(id)))
          }
          ordered
        />

        {currentInvoiceDetails.length === 0 && invoiceDetails.length > 0 && (
          <div className={clsx(classes.textGray500, classes.middleText, classes.textAlignCenter)}>
            {strings.noInvoiceForPayerMark}
          </div>
        )}
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={() => setStep(step - 1)}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="submit"
          disabled={selectedIds.length === 0}
          text={formStrings.next}
          style={{ width: 90 }}
          onClick={() => {
            setStep(step + 1);
          }}
        />
      </ActionableDialogActions>
    </div>
  );
};

const InvoicePreviewStep: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { bookingInquiry } = useContext(BookingDetailsContext);

  const {
    id,
    type,
    invoiceChargePayDayForm,
    invoiceInfoForm,
    invoiceDefaultValue,
    selectedInvoiceDetails,
    step,
    setStep,
    setInvoiceErrorMessage,
    invoice,
    setInvoice,
    refresh,
  } = useContext(InvoiceFormDialogContext);

  const pdfPreview = dashboardPdfApis.usePost('/dashboard/pdf/preview/Invoice');

  const submitMutation = useMutation(async (invoice_status: InvoiceStatusName) => {
    setInvoice(undefined);
    setInvoiceErrorMessage(undefined);

    try {
      const invoice_id_list = selectedInvoiceDetails.map(({ id }) => id);
      const salesperson_name = invoiceDefaultValue?.salesperson_name!;

      if (type === 'create') {
        const invoice = await dashboardInvoiceApis.client.post('/dashboard/invoice', {
          ...invoiceChargePayDayForm!,
          ...invoiceInfoForm!,
          salesperson_name,
          invoice_status,
          booking_inquiry: bookingInquiry.id,
          invoice_id_list,
        });

        setInvoice(invoice);
      } else if (type === 'update') {
        const invoice = await dashboardInvoiceApis.client.patch(
          '/dashboard/invoice/:id',
          {
            ...invoiceChargePayDayForm!,
            ...invoiceInfoForm!,
            invoice_status,
            invoice_id_list,
          },
          {
            params: {
              id: id!,
            },
          },
        );

        setInvoice(invoice);
      }

      setStep(step + 1);
    } catch (e) {
      if (
        isErrorFromPath(dashboardInvoiceApis.client.api, 'post', '/dashboard/invoice', e) ||
        isErrorFromPath(dashboardInvoiceApis.client.api, 'patch', '/dashboard/invoice/:id', e)
      ) {
        const { data } = e.response;

        setInvoiceErrorMessage(data);
        setStep(step + 1);
        refresh();
      } else {
        throw e;
      }
    } finally {
      refresh();
    }
  });

  const pdfUrl = useBlobURL(pdfPreview.data);

  useEffect(() => {
    pdfPreview.mutateAsync({
      ...invoiceChargePayDayForm!,
      ...invoiceInfoForm!,
      status:
        type === 'create'
          ? []
          : [
              ...(invoice?.status ?? []),
              { name: '発行済', message: '', time: new Date().toISOString() },
            ],
      d2d_charge_no: '',
      invoice_details: selectedInvoiceDetails,
    });
  }, []);

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [pdfPreview, submitMutation] });

  return (
    <div className={classes.form}>
      <div className={classes.dialogContent}>
        {renderAlertsAndSpinnerModal()}

        <div className={classes.flexColumnCenter}>
          {pdfUrl && (
            <Document file={pdfUrl} loading="">
              <Page width={900} pageNumber={1} />
            </Document>
          )}
        </div>
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={() => setStep(step - 1)}
        />

        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.temporarilySave}
          style={{ width: 90 }}
          onClick={() => submitMutation.mutateAsync(type === 'create' ? '作成中' : '修正中')}
        />

        <Button
          color="primary"
          size="small"
          shape="round"
          type="submit"
          text={formStrings.submit}
          style={{ width: 90 }}
          onClick={() => submitMutation.mutateAsync('発行済')}
        />
      </ActionableDialogActions>
    </div>
  );
};

const InvoiceResultStep = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { type, invoiceErrorMessage, invoice, onClose, onEditInvoice, step, setStep } =
    useContext(InvoiceFormDialogContext);

  return (
    <div className={classes.form}>
      <div className={classes.dialogContent}>
        <div className={classes.resultContainer}>
          {invoice?.invoice_status === '発行済' && strings.postInvoiceSuccessMessage(invoice)}
          {(invoice?.invoice_status === '作成中' || invoice?.invoice_status === '修正中') &&
            strings.postInvoiceDraftMessage()}
          {invoiceErrorMessage && strings.postInvoiceErrorMessage(invoiceErrorMessage.message)}
        </div>
      </div>

      <ActionableDialogActions justifyContent="center">
        {invoice && !invoiceErrorMessage && (
          <>
            <Button
              color="primary"
              size="small"
              shape="round"
              text={formStrings.ok}
              style={{ width: 90 }}
              onClick={onClose}
            />
          </>
        )}

        {invoiceErrorMessage && (
          <>
            <Button
              color="primary"
              size="small"
              shape="round"
              type="button"
              text={formStrings.prev}
              style={{ width: 90 }}
              onClick={() => {
                if (type === 'create') {
                  onEditInvoice?.(invoiceErrorMessage.invoice.id);
                } else {
                  setStep(step - 1);
                }
              }}
            />
            <Button
              color="primary"
              size="small"
              shape="round"
              type="button"
              text={formStrings.ok}
              style={{ width: 90 }}
              onClick={onClose}
            />
          </>
        )}
      </ActionableDialogActions>
    </div>
  );
};

export const InvoiceFormDialog: React.FC<InvoiceFormDialogContextValuesBase> = (props) => {
  const { type, open, onClose, onEditInvoice, id } = props;

  const [step, setStep] = useState(0);

  const { bookingInquiry } = useContext(BookingDetailsContext);

  const queryClient = useQueryClient();

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(
      dashboardInvoiceApis.getKeyByPath('get', '/dashboard/booking-inquiry/:d2d_no/invoice-detail'),
    );
    queryClient.invalidateQueries(dashboardInvoiceApis.getKeyByPath('get', '/dashboard/invoice'));
    queryClient.invalidateQueries(
      dashboardInvoiceApis.getKeyByPath('get', '/dashboard/invoice/:id'),
    );
    queryClient.invalidateQueries(
      dashboardBookingInquiryApis.getKeyByPath(
        'get',
        '/dashboard/booking-inquiry/:d2d_no/status-flows',
      ),
    );
    queryClient.invalidateQueries(
      invoiceApis.getKeyByPath('get', '/invoice/v3/:visit_id/download'),
    );
    queryClient.invalidateQueries(['/dashboard/tracking-data/', bookingInquiry.d2d_no]);
  }, [queryClient]);

  const invoiceDefaultValueQuery = dashboardInvoiceApis.useGet(
    '/dashboard/booking-inquiry/:d2d_no/invoice-default-value',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
    },
    {
      enabled: open,
    },
  );

  const currencyRatesQuery = exchangeCalendarApis.useGet(
    '/exchange-calendar',
    {
      queries: {
        pair: 'JPY_USD',
        start: dayjs
          .min(
            dayjs(bookingInquiry.created_at),
            dayjs(invoiceDefaultValueQuery.data?.conversion_rate_date ?? bookingInquiry.created_at),
          )!
          .format('YYYY-MM-DD'),
        end: dayjs
          .max(dayjs(), dayjs(invoiceDefaultValueQuery.data?.conversion_rate_date ?? undefined))!
          .format('YYYY-MM-DD'),
      },
    },
    {
      enabled: open && !!invoiceDefaultValueQuery.data,
    },
  );

  const invoiceQuery = dashboardInvoiceApis.useGet(
    '/dashboard/invoice/:id',
    {
      params: {
        id: id!,
      },
    },
    {
      enabled: open && type === 'update',
    },
  );

  const invoiceDetailsQuery = dashboardInvoiceApis.useGet(
    '/dashboard/booking-inquiry/:d2d_no/invoice-detail',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
      queries: { show_all: true },
    },
    {
      enabled: open,
    },
  );

  const [invoiceChargePayDayForm, setInvoiceChargePayDayForm] = useStateFromQuery(
    invoiceQuery,
    (d) => d && InvoiceHelper.mapInvoiceToChargePayDayForm(d),
  );
  const [invoiceInfoForm, setInvoiceInfoForm] = useStateFromQuery(
    invoiceQuery,
    (d) => d && InvoiceHelper.mapInvoiceToInfoForm(d),
  );
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useStateFromQuery(
    invoiceDetailsQuery,
    (d) => (d ? d.filter(({ invoice }) => !!id && invoice === id) : []),
  );

  useEffect(() => {
    if (invoiceDetailsQuery.data) {
      // Update selected invoice details in case the remote changes.
      setSelectedInvoiceDetails((selectedItems) =>
        selectedItems.flatMap(
          (selectedItem) =>
            mapOptional(
              invoiceDetailsQuery.data.find((d) => d.id === selectedItem.id),
              (d) => [d],
            ) ?? [],
        ),
      );
    }
  }, [invoiceDetailsQuery.data]);

  const [invoiceErrorMessage, setInvoiceErrorMessage] = useState<InvoiceErrorMessage>();
  const [invoice, setInvoice] = useStateFromQuery<Invoice | undefined>(invoiceQuery, (d) => d);

  const contextValue = useShallowMemo(
    (): InvoiceFormDialogContextValues => ({
      type,
      open,
      onClose,
      id,
      onEditInvoice,
      step,
      setStep,
      invoiceChargePayDayForm,
      setInvoiceChargePayDayForm,
      invoiceInfoForm,
      setInvoiceInfoForm,
      invoiceDefaultValue: invoiceDefaultValueQuery.data,
      currencyRates: currencyRatesQuery.data ?? [],
      invoiceDetails: invoiceDetailsQuery.data ?? [],
      selectedInvoiceDetails,
      setSelectedInvoiceDetails,
      invoiceErrorMessage,
      setInvoiceErrorMessage,
      invoice,
      setInvoice,
      refresh,
    }),
  );

  return (
    <InvoiceFormDialogContext.Provider value={contextValue}>
      <ActionableDialog open={open} onClose={onClose} size="lg" source={import.meta.url}>
        <ActionableDialogTitle textAlign="center">{strings.title[type]}</ActionableDialogTitle>

        <QueryContainer
          queries={[
            invoiceDefaultValueQuery,
            currencyRatesQuery,
            invoiceDetailsQuery,
            invoiceQuery,
          ]}
        >
          {step === 0 && <InvoiceChargePayDayStep />}
          {step === 1 && <InvoiceInfoStep />}
          {step === 2 && <InvoiceDetailsStep />}
          {step === 3 && <InvoicePreviewStep />}
          {step === 4 && <InvoiceResultStep />}
        </QueryContainer>
      </ActionableDialog>
    </InvoiceFormDialogContext.Provider>
  );
};
